import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Plus, Trash2, Pencil, RefreshCw, FileDown, FileSpreadsheet, Upload, Loader2, CheckCircle2, AlertTriangle, Wallet, Search, Landmark, HandCoins, ChevronsUpDown, ChevronUp, ChevronDown, ArrowUpDown, ListChecks, Building2, Receipt, CircleCheckBig, History, Settings } from "lucide-react";
import { RiconciliazioneCompagnieTab } from "@/pages/contabilita/RiconciliazioneCompagnieTab";
import { DateInputIT } from "@/components/DateInputIT";
import { fmtIT } from "@/lib/dateIT";

const euro = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);
const daysOpen = (d) => { if (!d) return 0; const ms = Date.now() - new Date(d + "T00:00:00").getTime(); return Math.max(0, Math.floor(ms / 86400000)); };

function DateActionButton({ icon: Icon, title, color, testid, busy, onConfirm }) {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(today());
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setD(today()); }}>
      <PopoverTrigger asChild>
        <Button size="icon" variant="ghost" className={`h-7 w-7 ${color}`} disabled={busy} title={title} data-testid={testid}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-3" align="end">
        <p className="text-xs font-semibold text-slate-700 mb-2">{title}</p>
        <Label className="text-[11px] text-slate-500">Data effettiva</Label>
        <DateInputIT value={d} onChange={(v) => setD(v)} className="mb-2 mt-1 block" testid={`${testid}-date`} />
        <Button size="sm" disabled={!d} className="w-full bg-teal-600 hover:bg-teal-700 text-white" onClick={() => { onConfirm(d); setOpen(false); }} data-testid={`${testid}-confirm`}>Conferma</Button>
      </PopoverContent>
    </Popover>
  );
}
const EMPTY = {
  data: today(), causale: "INCASSO", compagnia: "", numero_polizza: "", cliente: "", produttore: "",
  sede: "", modalita_pagamento: "contante", entrata: "", uscita: "", provvigioni: "", sospeso: "",
  data_versamento_agenzia: "", data_versamento_banca: "", stato_versamento: "da_versare",
  stato_operativo: "regolare", residuo: "", sospeso_note: "", note: "", data_effetto: "",
};

const OPER_STYLE = {
  sospeso: "bg-red-100 text-red-700",
  sospeso_parziale: "bg-orange-100 text-orange-700",
  sospeso_cofidis: "bg-fuchsia-100 text-fuchsia-700",
  in_transito: "bg-sky-100 text-sky-700",
  bonifico_da_verificare: "bg-amber-100 text-amber-700",
};

function Stat({ label, value, accent, danger }) {
  const cls = danger ? "border-red-300 bg-red-50 text-red-700" : accent ? "border-teal-700 bg-teal-700 text-white" : "border-slate-200 bg-white";
  return (
    <div className={`rounded-xl border p-3 ${cls}`} data-testid={`pnc-stat-${label}`}>
      <div className={`text-[11px] uppercase tracking-wide ${danger ? "text-red-500" : accent ? "text-teal-100" : "text-slate-400"}`}>{label}</div>
      <div className="text-lg font-bold mt-0.5">{value}</div>
    </div>
  );
}

export function PrimaNotaCassaTab() {
  const [meta, setMeta] = useState(null);
  const [dal, setDal] = useState(firstOfMonth());
  const [al, setAl] = useState(today());
  const [q, setQ] = useState("");
  const [fCausale, setFCausale] = useState("");
  const [fSede, setFSede] = useState("");
  const [fStato, setFStato] = useState("");
  const [fStatoOper, setFStatoOper] = useState("");
  const [soloDaVersare, setSoloDaVersare] = useState(false);
  const [soloSospesi, setSoloSospesi] = useState(false);
  const [sospesi, setSospesi] = useState({ per_stato: [], totale_importo: 0, totale_count: 0 });
  const [view, setView] = useState("movimenti");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [managers, setManagers] = useState([]);
  const [newMgr, setNewMgr] = useState("");
  const [giorni, setGiorni] = useState(7);
  const [sogliaImporto, setSogliaImporto] = useState(1000000);
  const [sogliaSaldo, setSogliaSaldo] = useState(1000000);
  const [storicoOpen, setStoricoOpen] = useState(false);
  const [storico, setStorico] = useState({ items: [], count: 0, totale: 0, operatori: [] });
  const [stoDal, setStoDal] = useState("");
  const [stoAl, setStoAl] = useState("");
  const [stoOp, setStoOp] = useState("");
  const [daVersare, setDaVersare] = useState({ per_sede: [], totale: 0, n_totale: 0 });
  const [premi, setPremi] = useState({ subagenti: [], totali: {}, can_manage: false });
  const [pdvOpen, setPdvOpen] = useState(false);
  const [pdvProd, setPdvProd] = useState("");
  const [pdvDet, setPdvDet] = useState({ operazioni: [], totale: 0, n: 0, can_manage: false });
  const [pdvSel, setPdvSel] = useState(new Set());
  const [pdvBusy, setPdvBusy] = useState(false);
  const [state, setState] = useState({ movimenti: [], saldo_iniziale: 0, saldo_finale: 0, riepilogo: {}, soglia_importo: 1000000 });
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const [sort, setSort] = useState({ key: "", dir: "asc" });
  const toggleSort = (key) => setSort((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  // riconciliazione
  const [ricData, setRicData] = useState(today());
  const [ric, setRic] = useState(null);
  const [contato, setContato] = useState("");
  const [nota, setNota] = useState("");
  // import
  const [impOpen, setImpOpen] = useState(false);
  const [impFile, setImpFile] = useState(null);
  const [impPreview, setImpPreview] = useState(null);
  const [impBusy, setImpBusy] = useState(false);

  useEffect(() => { api.get("/pnc/meta").then(({ data }) => setMeta(data)).catch(() => toast.error("Errore caricamento")); }, []);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (dal) params.dal = dal;
      if (al) params.al = al;
      if (q) params.q = q;
      if (fCausale) params.causale = fCausale;
      if (fSede) params.sede = fSede;
      if (fStato) params.stato_versamento = fStato;
      if (soloDaVersare) params.da_versare = true;
      if (fStatoOper) params.stato_operativo = fStatoOper;
      if (soloSospesi) params.solo_sospesi = true;
      const { data } = await api.get("/pnc/movimenti", { params });
      setState(data);
      api.get("/pnc/da-versare-per-sede").then(({ data: dv }) => setDaVersare(dv)).catch(() => {});
      api.get("/pnc/sospesi-riepilogo").then(({ data: sr }) => setSospesi(sr)).catch(() => {});
      api.get("/pnc/premi-da-versare", { params: { sede: fSede || undefined, dal: dal || undefined, al: al || undefined } }).then(({ data: pv }) => setPremi(pv)).catch(() => {});
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [dal, al, q, fCausale, fSede, fStato, fStatoOper, soloDaVersare, soloSospesi]);

  useEffect(() => { if (meta) load(); }, [meta, dal, al, fCausale, fSede, fStato, fStatoOper, soloDaVersare, soloSospesi, load]);

  const loadRic = useCallback(async () => {
    try {
      const { data } = await api.get("/pnc/riconciliazione", { params: { data: ricData } });
      setRic(data);
      setContato(data.quadratura ? String(data.quadratura.contato) : "");
      setNota(data.quadratura?.nota || "");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [ricData]);
  useEffect(() => { if (meta) loadRic(); }, [meta, ricData, loadRic]);

  const openNew = () => { setForm({ ...EMPTY, data: al || today() }); setEditId(null); setDlg(true); };
  const openEdit = (m) => {
    setForm({
      data: m.data || today(), causale: m.causale || "INCASSO", compagnia: m.compagnia || "",
      numero_polizza: m.numero_polizza || "", cliente: m.cliente || "", produttore: m.produttore || "",
      sede: m.sede || "", modalita_pagamento: m.modalita_pagamento || "contante",
      entrata: m.entrata ?? "", uscita: m.uscita ?? "", provvigioni: m.provvigioni ?? "", sospeso: m.sospeso ?? "",
      data_versamento_agenzia: m.data_versamento_agenzia || "", data_versamento_banca: m.data_versamento_banca || "",
      stato_versamento: m.stato_versamento || "da_versare",
      stato_operativo: m.stato_operativo || "regolare", residuo: m.residuo ?? "", sospeso_note: m.sospeso_note || "",
      note: m.note || "", data_effetto: m.data_effetto || "",
    });
    setEditId(m.id); setDlg(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, entrata: parseFloat(form.entrata || 0), uscita: parseFloat(form.uscita || 0),
        provvigioni: parseFloat(form.provvigioni || 0), sospeso: parseFloat(form.sospeso || 0),
        residuo: parseFloat(form.residuo || 0) };
      if (editId) await api.put(`/pnc/movimenti/${editId}`, body);
      else await api.post("/pnc/movimenti", body);
      toast.success(editId ? "Movimento aggiornato" : "Movimento registrato");
      setDlg(false); load(); loadRic();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare il movimento?")) return;
    try { await api.delete(`/pnc/movimenti/${id}`); load(); loadRic(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const segnaVersato = async (m, dataBanca) => {
    setBusy(m.id);
    try {
      await api.post(`/pnc/movimenti/${m.id}/versamento`, { data_versamento_banca: dataBanca || today(), stato_versamento: "versato_banca" });
      toast.success("Segnato come versato in banca"); load(); loadRic();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const segnaConsegnato = async (m, dataAgenzia) => {
    setBusy(m.id);
    try {
      await api.post(`/pnc/movimenti/${m.id}/versamento`, { data_versamento_agenzia: dataAgenzia || today(), stato_versamento: "consegnato_agenzia" });
      toast.success("Segnato come consegnato in agenzia"); load(); loadRic();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const syncFc = async () => {
    setBusy("sync");
    try {
      const { data } = await api.post("/pnc/sync-fc", null, { params: { dal, al } });
      toast.success(`Sincronizzati dai Fogli Cassa: ${data.creati} nuovi, ${data.aggiornati} aggiornati`);
      load(); loadRic();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const versaMassivoSede = async (sede) => {
    if (!window.confirm(`Segnare come versate in banca (oggi) tutte le rimesse aperte della sede "${sede}"?`)) return;
    setBusy("massivo");
    try {
      const { data } = await api.post("/pnc/versa-massivo", { sede });
      toast.success(`${data.versati} rimesse versate in banca`); load(); loadRic();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const salvaQuadratura = async () => {    setBusy("quad");
    try {
      await api.post("/pnc/quadratura", { data: ricData, contato, nota });
      toast.success("Quadratura salvata"); loadRic();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const exportFile = async (fmt) => {
    try {
      const { data: blob } = await api.get(`/pnc/export/${fmt}`, { params: { dal, al }, responseType: "blob" });
      const url = URL.createObjectURL(blob);
      if (fmt === "pdf") window.open(url, "_blank");
      else { const a = document.createElement("a"); a.href = url; a.download = "prima_nota_cassa.xlsx"; a.click(); }
    } catch { toast.error("Errore export"); }
  };

  const exportAnomali = async () => {
    try {
      const { data: blob } = await api.get("/pnc/anomali/export", { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "movimenti_anomali.xlsx"; a.click();
    } catch { toast.error("Errore export anomali"); }
  };

  const openDistinta = async () => {
    try {
      const { data: blob } = await api.get("/pnc/distinta-versamento/pdf", { params: { sede: fSede || undefined, dal, al }, responseType: "blob" });
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error("Errore distinta di versamento"); }
  };

  const openSospesiPdf = async () => {
    try {
      const { data: blob } = await api.get("/pnc/sospesi/pdf", { responseType: "blob" });
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error("Errore report sospesi"); }
  };

  const openReportGiornaliero = async () => {
    const d = window.prompt("Data del report cassa (AAAA-MM-GG):", today());
    if (!d) return;
    try {
      const { data: blob } = await api.get("/pnc/report-giornaliero/pdf", { params: { data: d, sede: fSede || undefined }, responseType: "blob" });
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error("Errore report giornaliero"); }
  };

  const fetchPdvDet = async (prod) => {
    const { data } = await api.get("/pnc/premi-da-versare/dettaglio", { params: { produttore: prod, sede: fSede || undefined, dal: dal || undefined, al: al || undefined } });
    return data;
  };

  const openPremiDettaglio = async (prod) => {
    setPdvProd(prod);
    try {
      const data = await fetchPdvDet(prod);
      setPdvDet(data);
      setPdvSel(new Set((data.operazioni || []).map((o) => o.id)));
      setPdvOpen(true);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const pdvToggle = (id) => setPdvSel((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const pdvToggleAll = () => setPdvSel((prev) => prev.size === (pdvDet.operazioni || []).length ? new Set() : new Set((pdvDet.operazioni || []).map((o) => o.id)));

  const pdvSegna = async (stato) => {
    const ids = [...pdvSel];
    if (!ids.length) { toast.error("Seleziona almeno un'operazione"); return; }
    const label = stato === "consegnato_agenzia" ? "consegnate in agenzia" : "versate in banca";
    if (!window.confirm(`Segnare ${ids.length} operazioni come ${label}? Il debito relativo di ${pdvProd} verrà azzerato.`)) return;
    setPdvBusy(true);
    try {
      const { data } = await api.post("/pnc/premi-da-versare/segna-versato", { ids, stato });
      toast.success(`${data.updated} operazioni aggiornate`);
      const det = await fetchPdvDet(pdvProd);
      setPdvDet(det);
      setPdvSel(new Set((det.operazioni || []).map((o) => o.id)));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPdvBusy(false); }
  };

  const pdvDistinta = async () => {
    const ids = [...pdvSel];
    try {
      const { data: blob } = await api.get("/pnc/premi-da-versare/distinta/pdf", { params: { produttore: pdvProd, ids: ids.length ? ids.join(",") : undefined, sede: fSede || undefined, dal: dal || undefined, al: al || undefined }, responseType: "blob" });
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error("Errore distinta"); }
  };

  const chiudiSospeso = async (m) => {
    const isCof = m.stato_operativo === "sospeso_cofidis";
    const motivo = window.prompt("Motivo chiusura del sospeso:", isCof ? "Bonifico Cofidis/Compass ricevuto" : "");
    if (motivo === null) return;
    setBusy(m.id);
    try {
      await api.post(`/pnc/movimenti/${m.id}/chiudi-sospeso`, { motivo });
      toast.success("Sospeso chiuso");
      load(); loadRic();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const openSettings = async () => {
    setSettingsOpen(true);
    try {
      const [{ data: mg }, { data: st }, { data: gr }] = await Promise.all([
        api.get("/pnc/sospesi-managers"), api.get("/pnc/sospesi-settings"), api.get("/pnc/config/guardrail")]);
      setManagers(mg.emails || []); setGiorni(st.giorni_scadenza || 7);
      setSogliaImporto(gr.soglia_importo || 1000000); setSogliaSaldo(gr.soglia_saldo || 1000000);
    } catch { toast.error("Errore caricamento impostazioni"); }
  };
  const saveGuardrail = async () => {
    try {
      await api.put("/pnc/config/guardrail", { soglia_importo: parseFloat(sogliaImporto), soglia_saldo: parseFloat(sogliaSaldo) });
      toast.success("Soglie di controllo salvate"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveManagers = async (list) => {
    try { await api.post("/pnc/sospesi-managers", { emails: list }); setManagers(list); toast.success("Autorizzati aggiornati"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addMgr = () => {
    const e = newMgr.trim().toLowerCase();
    if (!e) return;
    if (managers.includes(e)) { toast.error("Email già presente"); return; }
    saveManagers([...managers, e]); setNewMgr("");
  };
  const removeMgr = (e) => saveManagers(managers.filter((x) => x !== e));
  const saveGiorni = async () => {
    try { await api.post("/pnc/sospesi-settings", { giorni_scadenza: parseInt(giorni, 10) }); toast.success("Soglia scadenza salvata"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const loadStorico = useCallback(async () => {
    try {
      const { data } = await api.get("/pnc/sospesi/storico", { params: { dal: stoDal || undefined, al: stoAl || undefined, operatore: stoOp || undefined } });
      setStorico(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [stoDal, stoAl, stoOp]);
  useEffect(() => { if (storicoOpen) loadStorico(); }, [storicoOpen, loadStorico]);
  const openStorico = () => setStoricoOpen(true);

  const doImport = async (confirm) => {
    if (!impFile) { toast.error("Seleziona un file .xlsx"); return; }
    setImpBusy(true);
    try {
      const fd = new FormData(); fd.append("file", impFile);
      const { data } = await api.post(`/pnc/import-storico?dry_run=${confirm ? "false" : "true"}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      if (confirm) { toast.success(`Importate ${data.inseriti} righe storiche`); setImpOpen(false); setImpFile(null); setImpPreview(null); load(); }
      else { setImpPreview(data); toast.success(`Anteprima: ${data.totale_righe} righe rilevate`); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setImpBusy(false); }
  };

  if (!meta) return <div className="p-10 text-center text-slate-400">Caricamento…</div>;
  const r = state.riepilogo || {};
  const isDir = meta.is_direzione;
  const canClose = meta.can_close_sospesi;

  const NUM_KEYS = ["entrata", "uscita", "saldo_progressivo"];
  const sortedMov = (() => {
    if (!sort.key) return state.movimenti;
    const arr = [...state.movimenti].sort((a, b) => {
      const va = a[sort.key], vb = b[sort.key];
      if (NUM_KEYS.includes(sort.key)) return (Number(va) || 0) - (Number(vb) || 0);
      return String(va ?? "").toLowerCase().localeCompare(String(vb ?? "").toLowerCase(), "it", { numeric: true });
    });
    return sort.dir === "desc" ? arr.reverse() : arr;
  })();
  const SortTh = ({ label, k, className = "" }) => {
    const active = sort.key === k;
    const Icon = !active ? ChevronsUpDown : sort.dir === "asc" ? ChevronUp : ChevronDown;
    return (
      <TableHead className={className}>
        <button type="button" onClick={() => toggleSort(k)} data-testid={`pnc-sort-${k}`}
          className={`inline-flex items-center gap-1 ${className.includes("text-right") ? "flex-row-reverse" : ""} ${active ? "text-teal-600 font-semibold" : "text-slate-600"} hover:text-teal-700`}>
          {label}<Icon className={`h-3.5 w-3.5 ${active ? "opacity-100" : "opacity-40"}`} />
        </button>
      </TableHead>
    );
  };

  return (
    <div data-testid="pnc-tab">
      {/* Sotto-navigazione */}
      <div className="flex items-center gap-1 mb-5 border-b border-slate-200">
        <button onClick={() => setView("movimenti")} data-testid="pnc-view-movimenti"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${view === "movimenti" ? "border-teal-600 text-teal-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
          <Wallet className="h-4 w-4 mr-1 inline" /> Movimenti
        </button>
        <button onClick={() => setView("compagnie")} data-testid="pnc-view-compagnie"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${view === "compagnie" ? "border-teal-600 text-teal-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
          <Building2 className="h-4 w-4 mr-1 inline" /> Riconciliazione Compagnie
        </button>
      </div>

      {view === "compagnie" ? (
        <RiconciliazioneCompagnieTab meta={meta} onChanged={load} />
      ) : (
      <>
      {/* Cruscotto sospesi */}
      {sospesi.totale_count > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4 rounded-xl border border-red-200 bg-red-50/60 p-3" data-testid="pnc-sospesi-cruscotto">
          <span className="text-xs font-semibold uppercase tracking-wide text-red-700 flex items-center gap-1"><ListChecks className="h-4 w-4" /> Sospesi da recuperare</span>
          {sospesi.per_stato.map((s) => (
            <button key={s.stato} onClick={() => { setFStatoOper((v) => v === s.stato ? "" : s.stato); setSoloSospesi(false); }}
              className={`rounded-lg border px-3 py-1.5 text-sm transition-colors ${fStatoOper === s.stato ? "border-red-500 bg-white ring-1 ring-red-300" : "border-red-200 bg-white hover:bg-red-100"}`}
              data-testid={`pnc-sospeso-chip-${s.stato}`}>
              <span className="font-medium text-slate-800">{s.label}</span> · <span className="text-red-700 font-semibold">{euro(s.importo)}</span> <span className="text-slate-400">({s.count})</span>
            </button>
          ))}
          <span className="ml-auto text-sm font-bold text-red-800">Totale aperto: {euro(sospesi.totale_importo)} · {sospesi.totale_count} mov.</span>
          {sospesi.totale_scaduti > 0 && (
            <Badge className="bg-red-600 text-white border-0" data-testid="pnc-sospesi-scaduti">
              <AlertTriangle className="h-3 w-3 mr-1" /> {sospesi.totale_scaduti} oltre {sospesi.giorni_scadenza} gg
            </Badge>
          )}
          <Button size="sm" variant="outline" className="h-7 border-red-300 text-red-700 hover:bg-red-100" onClick={openSospesiPdf} data-testid="pnc-sospesi-pdf"><FileDown className="h-3.5 w-3.5 mr-1" /> Report PDF</Button>
          {fStatoOper && <Button size="sm" variant="ghost" className="text-red-600 h-7" onClick={() => setFStatoOper("")} data-testid="pnc-sospeso-clear">Azzera filtro</Button>}
        </div>
      )}

      {/* Cruscotto Premi da versare per subagente */}
      {premi.subagenti && premi.subagenti.length > 0 && (
        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3" data-testid="pnc-premi-da-versare">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-emerald-800 flex items-center gap-1"><HandCoins className="h-4 w-4" /> Premi da versare in agenzia (contanti + assegni)</span>
            <span className="ml-auto text-sm font-bold text-emerald-800" data-testid="pnc-premi-totale">Totale da versare: {euro(premi.totali?.da_versare || 0)} · {premi.totali?.n_da_versare || 0} mov.</span>
          </div>
          {premi.subagenti.some((s) => s.da_versare > 0) ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {premi.subagenti.filter((s) => s.da_versare > 0).map((s) => (
                <button key={s.produttore} onClick={() => openPremiDettaglio(s.produttore)}
                  className="text-left rounded-lg border border-emerald-300 bg-white px-3 py-2 hover:bg-emerald-100 transition-colors"
                  data-testid={`pnc-premi-sub-${s.produttore}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-slate-800 truncate">{s.produttore}</span>
                    <span className="text-emerald-700 font-bold">{euro(s.da_versare)}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Contanti {euro(s.da_versare_contanti)} · Assegni {euro(s.da_versare_assegni)} · {s.n_da_versare} mov.
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-emerald-700" data-testid="pnc-premi-vuoto">Nessun premio da versare: tutti gli incassi in contanti/assegni sono già stati consegnati.</p>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div><Label className="text-xs text-slate-500">Dal</Label><DateInputIT value={dal} onChange={(v) => setDal(v)} className="bg-white w-40" testid="pnc-dal" /></div>
        <div><Label className="text-xs text-slate-500">Al</Label><DateInputIT value={al} onChange={(v) => setAl(v)} className="bg-white w-40" testid="pnc-al" /></div>
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-[30px] h-4 w-4 text-slate-400" />
          <Label className="text-xs text-slate-500">Cerca</Label>
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Cliente, polizza, compagnia…" className="pl-9 bg-white" data-testid="pnc-q" />
        </div>
        <div><Label className="text-xs text-slate-500">Causale</Label>
          <select value={fCausale} onChange={(e) => setFCausale(e.target.value)} className="h-10 rounded-md border border-slate-300 bg-white px-2 text-sm block" data-testid="pnc-f-causale">
            <option value="">Tutte</option>{meta.causali.map((c) => <option key={c} value={c}>{c}</option>)}
          </select></div>
        <div><Label className="text-xs text-slate-500">Sede</Label>
          <select value={fSede} onChange={(e) => setFSede(e.target.value)} className="h-10 rounded-md border border-slate-300 bg-white px-2 text-sm block" data-testid="pnc-f-sede">
            <option value="">Tutte</option>{meta.sedi.map((s) => <option key={s} value={s}>{s}</option>)}
          </select></div>
        <div><Label className="text-xs text-slate-500 flex items-center gap-1"><ArrowUpDown className="h-3 w-3" /> Ordina per</Label>
          <select value={sort.key ? `${sort.key}:${sort.dir}` : ""} onChange={(e) => {
            const v = e.target.value;
            if (!v) { setSort({ key: "", dir: "asc" }); return; }
            const [key, dir] = v.split(":"); setSort({ key, dir });
          }} className="h-10 rounded-md border border-slate-300 bg-white px-2 text-sm block" data-testid="pnc-quick-sort">
            <option value="">— predefinito —</option>
            <option value="data:desc">Data (più recenti)</option>
            <option value="data:asc">Data (più vecchi)</option>
            <option value="cliente:asc">Cliente (A→Z)</option>
            <option value="cliente:desc">Cliente (Z→A)</option>
            <option value="produttore:asc">Intermediario (A→Z)</option>
            <option value="produttore:desc">Intermediario (Z→A)</option>
          </select></div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Button onClick={openNew} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="pnc-add"><Plus className="h-4 w-4 mr-1" /> Nuovo movimento</Button>
        <Button onClick={syncFc} disabled={busy === "sync"} variant="outline" data-testid="pnc-sync">{busy === "sync" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Sincronizza dai Fogli Cassa</Button>
        <Button onClick={() => exportFile("excel")} variant="outline" data-testid="pnc-excel"><FileSpreadsheet className="h-4 w-4 mr-1" /> Excel</Button>
        <Button onClick={() => exportFile("pdf")} variant="outline" data-testid="pnc-pdf"><FileDown className="h-4 w-4 mr-1" /> PDF</Button>
        <Button onClick={openDistinta} variant="outline" data-testid="pnc-distinta"><Receipt className="h-4 w-4 mr-1" /> Distinta versamento</Button>
        <Button onClick={openReportGiornaliero} variant="outline" data-testid="pnc-report-giornaliero"><FileDown className="h-4 w-4 mr-1" /> Report giornaliero</Button>
        {isDir && <Button onClick={() => { setImpOpen(true); setImpPreview(null); setImpFile(null); }} variant="outline" data-testid="pnc-import-btn"><Upload className="h-4 w-4 mr-1" /> Importa storico</Button>}
        <Button onClick={() => setSoloDaVersare((v) => !v)} variant={soloDaVersare ? "default" : "outline"} className={soloDaVersare ? "bg-amber-600 hover:bg-amber-700 text-white" : ""} data-testid="pnc-solo-da-versare">
          <Landmark className="h-4 w-4 mr-1" /> {soloDaVersare ? "Mostra tutti" : "Solo da versare"}
        </Button>
        <Button onClick={() => { setSoloSospesi((v) => !v); setFStatoOper(""); }} variant={soloSospesi ? "default" : "outline"} className={soloSospesi ? "bg-red-600 hover:bg-red-700 text-white" : ""} data-testid="pnc-solo-sospesi">
          <ListChecks className="h-4 w-4 mr-1" /> {soloSospesi ? "Mostra tutti" : "Solo sospesi"}
        </Button>
        <Button onClick={openStorico} variant="outline" data-testid="pnc-storico-btn"><History className="h-4 w-4 mr-1" /> Storico chiusure</Button>
        {isDir && <Button onClick={exportAnomali} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" data-testid="pnc-anomali-export-btn"><AlertTriangle className="h-4 w-4 mr-1" /> Anomali</Button>}
        {isDir && <Button onClick={openSettings} variant="outline" data-testid="pnc-settings-btn"><Settings className="h-4 w-4 mr-1" /> Impostazioni</Button>}
      </div>

      {/* Rimesse aperte per sede */}
      {daVersare.n_totale > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="pnc-da-versare-sedi">
          <span className="text-xs font-semibold uppercase tracking-wide text-amber-700 flex items-center gap-1"><Landmark className="h-4 w-4" /> Da versare</span>
          {daVersare.per_sede.map((s) => (
            <div key={s.sede} className="flex items-center rounded-lg border border-amber-300 bg-white overflow-hidden" data-testid={`pnc-sede-${s.sede}`}>
              <button onClick={() => { setFSede(s.sede === "—" ? "" : s.sede); setSoloDaVersare(true); }} className="px-3 py-1.5 text-sm hover:bg-amber-100 transition-colors">
                <span className="font-medium text-slate-800">{s.sede}</span> · <span className="text-amber-700 font-semibold">{euro(s.importo)}</span> <span className="text-slate-400">({s.count})</span>
              </button>
              <button onClick={() => versaMassivoSede(s.sede)} disabled={busy === "massivo"} title="Versa tutte in banca (oggi)" className="border-l border-amber-300 px-2 py-1.5 text-emerald-600 hover:bg-emerald-50 transition-colors" data-testid={`pnc-versa-tutti-${s.sede}`}>
                {busy === "massivo" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Landmark className="h-3.5 w-3.5" />}
              </button>
            </div>
          ))}
          <span className="ml-auto text-sm font-bold text-amber-800">Totale: {euro(daVersare.totale)} · {daVersare.n_totale} mov.</span>
        </div>
      )}

      {/* Riepilogo */}
      {state.saldo_anomalo && (
        <div className="mb-3 flex items-start gap-2 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-red-700" data-testid="pnc-saldo-anomalo-alert">
          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
          <div className="text-sm flex-1">
            <span className="font-semibold">Saldo anomalo rilevato.</span> Uno dei saldi supera la soglia di allerta ({euro(state.soglia_saldo)}).
            Potrebbero esserci movimenti con importi errati (es. un numero di polizza o una data digitati nel campo importo). Le righe anomale sono evidenziate in rosso nella tabella.
          </div>
          <Button size="sm" variant="outline" className="shrink-0 border-red-300 text-red-700 hover:bg-red-100" onClick={exportAnomali} data-testid="pnc-anomali-export"><FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Esporta anomali</Button>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        <Stat label="Saldo iniziale" value={euro(state.saldo_iniziale)} />
        <Stat label="Entrate" value={euro(r.totale_entrate)} />
        <Stat label="Uscite" value={euro(r.totale_uscite)} />
        <Stat label="Saldo cassa attuale" value={euro(state.saldo_finale)} accent />
        <Stat label="Provvigioni" value={euro(r.totale_provvigioni)} />
        <Stat label="Sospesi aperti" value={euro(sospesi.totale_importo)} danger />
        {(r.totale_versato_compagnia || 0) > 0 && (
          <Stat label="Versato alla Compagnia" value={euro(r.totale_versato_compagnia)} data-testid="pnc-stat-versato-compagnia" />
        )}
      </div>

      {/* Riconciliazione interna */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 mb-5" data-testid="pnc-ric-panel">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2 text-slate-700 mr-2"><Wallet className="h-4 w-4" /><h3 className="font-heading text-sm font-semibold">Riconciliazione interna cassa</h3></div>
          <div><Label className="text-xs text-slate-500">Giorno</Label><DateInputIT value={ricData} onChange={(v) => setRicData(v)} className="bg-white w-40" testid="pnc-ric-data" /></div>
          <div><Label className="text-xs text-slate-500">Saldo teorico contanti</Label><div className="h-10 flex items-center font-bold text-slate-800" data-testid="pnc-ric-teorico">{euro(ric?.saldo_teorico_contanti || 0)}</div></div>
          <div><Label className="text-xs text-slate-500">Contante contato €</Label><Input type="number" step="0.01" value={contato} onChange={(e) => setContato(e.target.value)} className="bg-white w-40" data-testid="pnc-ric-contato" /></div>
          {contato !== "" && (() => {
            const teor = ric?.saldo_teorico_contanti || 0;
            const diff = Math.round((parseFloat(contato || 0) - teor) * 100) / 100;
            return <Badge className={`h-10 px-3 flex items-center gap-1 border-0 ${diff === 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`} data-testid="pnc-ric-stato">
              {diff === 0 ? <><CheckCircle2 className="h-4 w-4" /> Quadra</> : <><AlertTriangle className="h-4 w-4" /> Disallineamento {euro(diff)}</>}
            </Badge>;
          })()}
          <div className="flex-1 min-w-[160px]"><Label className="text-xs text-slate-500">Nota</Label><Input value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Nota rettifica…" className="bg-white" data-testid="pnc-ric-nota" /></div>
          <Button onClick={salvaQuadratura} disabled={busy === "quad" || contato === ""} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="pnc-ric-save">Salva quadratura</Button>
        </div>
        {ric?.quadratura && <p className="text-xs text-slate-400 mt-2">Ultima quadratura: {ric.quadratura.stato} · diff {euro(ric.quadratura.differenza)} · {ric.quadratura.by}</p>}
      </div>

      {/* Tabella movimenti */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <SortTh label="Data" k="data" /><SortTh label="Causale" k="causale" /><SortTh label="Cliente/Descr." k="cliente" />
            <SortTh label="Compagnia" k="compagnia" /><SortTh label="Produttore" k="produttore" /><SortTh label="Sede" k="sede" />
            <SortTh label="Mod." k="modalita_pagamento" /><SortTh label="Entrata" k="entrata" className="text-right" />
            <SortTh label="Uscita" k="uscita" className="text-right" /><SortTh label="Saldo" k="saldo_progressivo" className="text-right" />
            <SortTh label="Versamento" k="stato_versamento" /><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {sortedMov.length === 0 ? (
              <TableRow><TableCell colSpan={12} className="text-center text-slate-400 py-10">Nessun movimento nel periodo. Usa "Sincronizza dai Fogli Cassa" o aggiungine uno.</TableCell></TableRow>
            ) : sortedMov.map((m) => {
              const aperto = m.stato_operativo && m.stato_operativo !== "regolare";
              const gg = aperto ? daysOpen(m.data) : 0;
              const scaduto = aperto && gg > (meta.sospesi_giorni_scadenza || 7);
              const sImp = state.soglia_importo || 1000000;
              const pol = (m.numero_polizza || "").replace(/\D/g, "");
              const anomalo = Number(m.entrata) > sImp || Number(m.uscita) > sImp ||
                (pol.length >= 5 && (String(Math.trunc(Number(m.entrata))) === pol || String(Math.trunc(Number(m.uscita))) === pol) && (Number(m.entrata) > 0 || Number(m.uscita) > 0));
              return (
              <TableRow key={m.id} data-testid={`pnc-row-${m.id}`} className={anomalo ? "bg-red-100 hover:bg-red-200" : scaduto ? "bg-red-50 hover:bg-red-100" : "hover:bg-slate-50"} title={anomalo ? "Importo anomalo: verifica di non aver inserito un numero di polizza o una data nel campo importo" : undefined}>
                <TableCell className="text-sm whitespace-nowrap">{fmtIT(m.data)}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{m.causale}</Badge>{m.origine === "fc" && <span className="ml-1 text-[10px] text-teal-600">FC</span>}{m.origine === "storico" && <span className="ml-1 text-[10px] text-slate-400">storico</span>}{m.origine === "trattativa" && <span className="ml-1 text-[10px] text-indigo-600" title={m.note} data-testid={`pnc-origine-trattativa-${m.id}`}>{(m.note || "").split(" — ")[0] || "trattativa"}</span>}</TableCell>
                <TableCell className="text-sm">{m.cliente || "—"}
                  {aperto && (
                    <Badge className={`ml-1 border-0 text-[10px] ${OPER_STYLE[m.stato_operativo] || "bg-slate-100 text-slate-600"}`} data-testid={`pnc-oper-${m.id}`}>
                      {meta.stati_operativi_label?.[m.stato_operativo] || m.stato_operativo}
                    </Badge>
                  )}
                  {scaduto && (
                    <Badge className="ml-1 border-0 text-[10px] bg-red-600 text-white" data-testid={`pnc-scaduto-${m.id}`} title={`Aperto da ${gg} giorni`}>{gg} gg</Badge>
                  )}
                </TableCell>
                <TableCell className="text-sm">{m.compagnia || "—"}</TableCell>
                <TableCell className="text-sm">{m.produttore || "—"}</TableCell>
                <TableCell className="text-sm">{m.sede || "—"}</TableCell>
                <TableCell className="text-sm">{meta.modalita_label[m.modalita_pagamento] || m.modalita_pagamento}</TableCell>
                <TableCell className="text-right text-sm text-emerald-600 font-medium">{m.entrata ? euro(m.entrata) : "—"}</TableCell>
                <TableCell className="text-right text-sm text-red-500">{m.uscita ? euro(m.uscita) : "—"}</TableCell>
                <TableCell className="text-right text-sm font-semibold">{euro(m.saldo_progressivo)}</TableCell>
                <TableCell>
                  {m.stato_versamento === "versato_compagnia" ? <Badge className="bg-sky-100 text-sky-700 border-0 text-xs" data-testid={`pnc-vers-compagnia-${m.id}`}>Versato alla Compagnia</Badge>
                    : m.stato_versamento === "versato_banca" ? <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">Versato</Badge>
                      : m.stato_versamento === "consegnato_agenzia" ? <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">In agenzia</Badge>
                        : <Badge variant="outline" className="text-xs text-slate-500">Da versare</Badge>}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {m.modalita_pagamento !== "pagato_compagnia" && m.stato_versamento === "da_versare" && (
                    <DateActionButton icon={HandCoins} title="Segna consegnato in agenzia" color="text-amber-600" testid={`pnc-consegnato-${m.id}`} busy={busy === m.id} onConfirm={(d) => segnaConsegnato(m, d)} />
                  )}
                  {m.modalita_pagamento !== "pagato_compagnia" && m.stato_versamento !== "versato_banca" && (
                    <DateActionButton icon={Landmark} title="Segna versato in banca" color="text-emerald-600" testid={`pnc-versato-${m.id}`} busy={busy === m.id} onConfirm={(d) => segnaVersato(m, d)} />
                  )}
                  {canClose && m.stato_operativo && m.stato_operativo !== "regolare" && (
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-green-600" disabled={busy === m.id} onClick={() => chiudiSospeso(m)} title={m.stato_operativo === "sospeso_cofidis" ? "Bonifico ricevuto → chiudi sospeso" : "Chiudi sospeso"} data-testid={`pnc-chiudi-sospeso-${m.id}`}>
                      {busy === m.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CircleCheckBig className="h-3.5 w-3.5" />}
                    </Button>
                  )}
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(m)} data-testid={`pnc-edit-${m.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => del(m.id)} data-testid={`pnc-del-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Dialog movimento */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Modifica movimento" : "Nuovo movimento cassa"}</DialogTitle>
            <DialogDescription>Registra un movimento di cassa (entrata/uscita) con causale, sede, modalità e tracciamento versamento.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 py-2">
            <div className="space-y-1"><Label className="text-xs">Data</Label><DateInputIT value={form.data} onChange={(v) => setForm({ ...form, data: v })} testid="pnc-f-data" /></div>
            <div className="space-y-1"><Label className="text-xs">Causale</Label>
              <select value={form.causale} onChange={(e) => setForm({ ...form, causale: e.target.value })} className="w-full h-10 rounded-md border border-slate-300 px-2 text-sm" data-testid="pnc-f-causale-dlg">
                {meta.causali.map((c) => <option key={c} value={c}>{c}</option>)}
              </select></div>
            <div className="space-y-1"><Label className="text-xs">Sede</Label>
              <select value={form.sede} onChange={(e) => setForm({ ...form, sede: e.target.value })} className="w-full h-10 rounded-md border border-slate-300 px-2 text-sm" data-testid="pnc-f-sede-dlg">
                <option value="">—</option>{meta.sedi.map((s) => <option key={s} value={s}>{s}</option>)}
              </select></div>
            <div className="space-y-1 col-span-2 md:col-span-1"><Label className="text-xs">Cliente / Descrizione</Label><Input value={form.cliente} onChange={(e) => setForm({ ...form, cliente: e.target.value })} data-testid="pnc-f-cliente" /></div>
            <div className="space-y-1"><Label className="text-xs">Compagnia</Label><Input value={form.compagnia} onChange={(e) => setForm({ ...form, compagnia: e.target.value })} data-testid="pnc-f-compagnia" /></div>
            <div className="space-y-1"><Label className="text-xs">N. polizza</Label><Input value={form.numero_polizza} onChange={(e) => setForm({ ...form, numero_polizza: e.target.value })} data-testid="pnc-f-polizza" /></div>
            <div className="space-y-1"><Label className="text-xs">Scadenza rata (facoltativa)</Label><DateInputIT value={form.data_effetto || ""} onChange={(v) => setForm({ ...form, data_effetto: v })} title="Data effetto del titolo: rende esatto l'abbinamento con il Foglio Cassa della compagnia (utile per i frazionamenti mensili)" testid="pnc-f-scadenza-rata" /></div>
            <div className="space-y-1"><Label className="text-xs">Produttore/Collaboratore</Label><Input value={form.produttore} onChange={(e) => setForm({ ...form, produttore: e.target.value })} data-testid="pnc-f-produttore" /></div>
            <div className="space-y-1"><Label className="text-xs">Modalità pagamento</Label>
              <select value={form.modalita_pagamento} onChange={(e) => {
                const mo = e.target.value;
                if (mo === "pagato_compagnia") setForm({ ...form, modalita_pagamento: mo, stato_versamento: "versato_compagnia", data_versamento_agenzia: "", data_versamento_banca: "" });
                else setForm({ ...form, modalita_pagamento: mo, stato_versamento: form.stato_versamento === "versato_compagnia" ? "da_versare" : form.stato_versamento });
              }} className="w-full h-10 rounded-md border border-slate-300 px-2 text-sm" data-testid="pnc-f-modalita">
                {meta.modalita.map((mo) => <option key={mo} value={mo}>{meta.modalita_label[mo]}</option>)}
              </select>
              {form.modalita_pagamento === "pagato_compagnia" && (
                <p className="text-[11px] text-sky-600">Incasso fuori cassa: non entra nel saldo cassa né tra i "da versare".</p>
              )}</div>
            <div className="space-y-1"><Label className="text-xs">Entrata €</Label><Input type="number" step="0.01" value={form.entrata} onChange={(e) => setForm({ ...form, entrata: e.target.value })} data-testid="pnc-f-entrata" /></div>
            <div className="space-y-1"><Label className="text-xs">Uscita €</Label><Input type="number" step="0.01" value={form.uscita} onChange={(e) => setForm({ ...form, uscita: e.target.value })} data-testid="pnc-f-uscita" /></div>
            <div className="space-y-1"><Label className="text-xs">Provvigioni €</Label><Input type="number" step="0.01" value={form.provvigioni} onChange={(e) => setForm({ ...form, provvigioni: e.target.value })} data-testid="pnc-f-provv" /></div>
            <div className="space-y-1"><Label className="text-xs">Sospeso €</Label><Input type="number" step="0.01" value={form.sospeso} onChange={(e) => setForm({ ...form, sospeso: e.target.value })} data-testid="pnc-f-sospeso" /></div>
            <div className="space-y-1"><Label className="text-xs">Vers. in agenzia</Label><DateInputIT value={form.data_versamento_agenzia} disabled={form.modalita_pagamento === "pagato_compagnia"} onChange={(v) => setForm({ ...form, data_versamento_agenzia: v })} testid="pnc-f-vers-ag" /></div>
            <div className="space-y-1"><Label className="text-xs">Vers. in banca</Label><DateInputIT value={form.data_versamento_banca} disabled={form.modalita_pagamento === "pagato_compagnia"} onChange={(v) => setForm({ ...form, data_versamento_banca: v })} testid="pnc-f-vers-bk" /></div>
            <div className="space-y-1"><Label className="text-xs">Stato versamento</Label>
              <select value={form.stato_versamento} disabled={form.modalita_pagamento === "pagato_compagnia"} onChange={(e) => setForm({ ...form, stato_versamento: e.target.value })} className="w-full h-10 rounded-md border border-slate-300 px-2 text-sm disabled:bg-slate-100 disabled:text-slate-500" data-testid="pnc-f-stato">
                {meta.stati_versamento.map((s) => <option key={s} value={s}>{meta.stati_versamento_label[s]}</option>)}
              </select></div>
            <div className="space-y-1"><Label className="text-xs">Stato operativo</Label>
              <select value={form.stato_operativo} onChange={(e) => setForm({ ...form, stato_operativo: e.target.value })} className="w-full h-10 rounded-md border border-slate-300 px-2 text-sm" data-testid="pnc-f-stato-oper">
                {(meta.stati_operativi || []).map((s) => <option key={s} value={s}>{meta.stati_operativi_label[s]}</option>)}
              </select></div>
            {form.stato_operativo === "sospeso_parziale" && (
              <div className="space-y-1"><Label className="text-xs text-orange-600">Residuo da recuperare €</Label><Input type="number" step="0.01" value={form.residuo} onChange={(e) => setForm({ ...form, residuo: e.target.value })} data-testid="pnc-f-residuo" /></div>
            )}
            {form.stato_operativo !== "regolare" && (
              <div className="space-y-1 col-span-2 md:col-span-3"><Label className="text-xs">Nota sospeso</Label><Input value={form.sospeso_note} onChange={(e) => setForm({ ...form, sospeso_note: e.target.value })} placeholder="Es. attesa bonifico Cofidis, assegno subagente…" data-testid="pnc-f-sospeso-note" /></div>
            )}
            <div className="space-y-1 col-span-2 md:col-span-3"><Label className="text-xs">Note</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="pnc-f-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="pnc-f-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editId ? "Aggiorna" : "Salva")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog import storico */}
      <Dialog open={impOpen} onOpenChange={setImpOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Importa Prima Nota storica</DialogTitle>
            <DialogDescription>Carica l'Excel "Prima Nota Cassa" di Antonio. Prima un'anteprima, poi conferma. La conferma <b>azzera e reimporta</b> lo storico precedente.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <Input type="file" accept=".xlsx,.xlsm" onChange={(e) => { setImpFile(e.target.files[0]); setImpPreview(null); }} data-testid="pnc-import-file" />
            {impPreview && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="pnc-import-preview">
                <p><b>{impPreview.totale_righe}</b> righe rilevate · Entrate {euro(impPreview.totale_entrate)} · Uscite {euro(impPreview.totale_uscite)}</p>
                <div className="mt-2 max-h-40 overflow-y-auto text-xs text-slate-600">
                  {(impPreview.sample || []).map((s, i) => <div key={i}>{fmtIT(s.data)} · {s.causale} · {s.cliente} · {euro(s.entrata || s.uscita)}</div>)}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => doImport(false)} disabled={impBusy || !impFile} data-testid="pnc-import-preview-btn">{impBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Anteprima"}</Button>
            <Button onClick={() => doImport(true)} disabled={impBusy || !impPreview} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="pnc-import-confirm">Conferma import</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog impostazioni sospesi (admin) */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-lg" data-testid="pnc-settings-dialog">
          <DialogHeader><DialogTitle className="font-heading">Impostazioni Prima Nota</DialogTitle>
            <DialogDescription>Gestione sospesi (autorizzati e soglia ritardo) e controlli sugli importi anomali.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold text-slate-700">Autorizzati a chiudere i sospesi</Label>
              <p className="text-[11px] text-slate-400 mb-2">La Direzione (admin) è sempre autorizzata. Aggiungi qui le email degli altri operatori (es. Antonio, Melania).</p>
              <div className="space-y-1.5 mb-2">
                {managers.length === 0 && <p className="text-xs text-slate-400">Nessun operatore aggiuntivo autorizzato.</p>}
                {managers.map((e) => (
                  <div key={e} className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5" data-testid={`pnc-mgr-${e}`}>
                    <span className="text-sm text-slate-700">{e}</span>
                    <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500" onClick={() => removeMgr(e)} data-testid={`pnc-mgr-remove-${e}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newMgr} onChange={(ev) => setNewMgr(ev.target.value)} onKeyDown={(ev) => ev.key === "Enter" && addMgr()} placeholder="email@assietica.it" className="bg-white" data-testid="pnc-mgr-input" />
                <Button onClick={addMgr} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="pnc-mgr-add"><Plus className="h-4 w-4 mr-1" /> Aggiungi</Button>
              </div>
            </div>
            <div className="border-t border-slate-100 pt-3">
              <Label className="text-xs font-semibold text-slate-700">Soglia "sospeso in ritardo" (giorni)</Label>
              <p className="text-[11px] text-slate-400 mb-2">I sospesi aperti da più di questi giorni vengono evidenziati in rosso.</p>
              <div className="flex gap-2 items-center">
                <Input type="number" min="1" max="365" value={giorni} onChange={(ev) => setGiorni(ev.target.value)} className="bg-white w-28" data-testid="pnc-giorni-input" />
                <Button onClick={saveGiorni} variant="outline" data-testid="pnc-giorni-save">Salva soglia</Button>
              </div>
            </div>
            <div className="border-t border-slate-100 pt-3">
              <Label className="text-xs font-semibold text-slate-700">Controllo importi anomali</Label>
              <p className="text-[11px] text-slate-400 mb-2">Previene errori di battitura (es. numero di polizza o data inseriti nel campo importo) e segnala saldi inconsistenti.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] text-slate-500">Importo massimo per movimento (€)</Label>
                  <Input type="number" min="1" step="1000" value={sogliaImporto} onChange={(ev) => setSogliaImporto(ev.target.value)} className="bg-white mt-1" data-testid="pnc-soglia-importo-input" />
                  <p className="text-[10px] text-slate-400 mt-1">Importi oltre questa soglia vengono rifiutati al salvataggio.</p>
                </div>
                <div>
                  <Label className="text-[11px] text-slate-500">Soglia allerta saldo (€)</Label>
                  <Input type="number" min="1" step="1000" value={sogliaSaldo} onChange={(ev) => setSogliaSaldo(ev.target.value)} className="bg-white mt-1" data-testid="pnc-soglia-saldo-input" />
                  <p className="text-[10px] text-slate-400 mt-1">Se un saldo supera questo valore compare un avviso sul cruscotto.</p>
                </div>
              </div>
              <Button onClick={saveGuardrail} variant="outline" className="mt-2" data-testid="pnc-soglie-save">Salva soglie di controllo</Button>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setSettingsOpen(false)}>Chiudi</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog storico chiusure */}
      <Dialog open={storicoOpen} onOpenChange={setStoricoOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="pnc-storico-dialog">
          <DialogHeader><DialogTitle className="font-heading">Storico chiusure sospesi</DialogTitle>
            <DialogDescription>Registro di chi ha chiuso quali sospesi e quando. Filtra per operatore e periodo.</DialogDescription></DialogHeader>
          <div className="flex flex-wrap items-end gap-2 py-2">
            <div><Label className="text-xs text-slate-500">Dal</Label><DateInputIT value={stoDal} onChange={(v) => setStoDal(v)} className="bg-white w-40" testid="pnc-storico-dal" /></div>
            <div><Label className="text-xs text-slate-500">Al</Label><DateInputIT value={stoAl} onChange={(v) => setStoAl(v)} className="bg-white w-40" testid="pnc-storico-al" /></div>
            <div><Label className="text-xs text-slate-500">Operatore</Label>
              <select value={stoOp} onChange={(e) => setStoOp(e.target.value)} className="h-10 rounded-md border border-slate-300 bg-white px-2 text-sm block w-52" data-testid="pnc-storico-op">
                <option value="">Tutti</option>{(storico.operatori || []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select></div>
            <span className="ml-auto text-sm text-slate-600">{storico.count} chiusure · {euro(storico.totale)}</span>
          </div>
          <div className="rounded-xl border border-slate-200 overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Chiuso il</TableHead><TableHead>Operatore</TableHead><TableHead>Cliente</TableHead>
                <TableHead>Tipo sospeso</TableHead><TableHead className="text-right">Importo</TableHead><TableHead>Motivo</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {storico.items.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">Nessuna chiusura nel periodo.</TableCell></TableRow>
                ) : storico.items.map((i) => (
                  <TableRow key={i.id} data-testid={`pnc-storico-row-${i.id}`}>
                    <TableCell className="text-xs whitespace-nowrap">{fmtIT(i.chiuso_il)}</TableCell>
                    <TableCell className="text-xs">{i.chiuso_da}</TableCell>
                    <TableCell className="text-xs">{i.cliente || "—"}</TableCell>
                    <TableCell className="text-xs">{i.stato_precedente_label}{i.tipo === "cofidis_auto" && <span className="ml-1 text-[10px] text-fuchsia-600">auto</span>}</TableCell>
                    <TableCell className="text-xs text-right">{euro(i.importo)}</TableCell>
                    <TableCell className="text-xs text-slate-500">{i.motivo || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setStoricoOpen(false)}>Chiudi</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog dettaglio Premi da versare per subagente */}
      <Dialog open={pdvOpen} onOpenChange={setPdvOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="pnc-premi-dialog">
          <DialogHeader><DialogTitle className="font-heading">Premi da versare — {pdvProd}</DialogTitle>
            <DialogDescription>Operazioni in contanti/assegni ancora da consegnare in agenzia. Seleziona le voci, genera la distinta e segnale come versate.</DialogDescription></DialogHeader>
          <div className="flex flex-wrap items-center gap-2 py-1">
            <Button size="sm" variant="outline" onClick={pdvToggleAll} data-testid="pnc-premi-toggle-all">
              {pdvSel.size === (pdvDet.operazioni || []).length && pdvDet.operazioni?.length > 0 ? "Deseleziona tutto" : "Seleziona tutto"}
            </Button>
            <span className="text-sm text-slate-600">{pdvSel.size} selezionate</span>
            <span className="ml-auto text-sm font-bold text-emerald-800" data-testid="pnc-premi-det-totale">Totale debito: {euro(pdvDet.totale || 0)} · {pdvDet.n || 0} mov.</span>
          </div>
          <div className="rounded-xl border border-slate-200 overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="w-8"></TableHead><TableHead>Data</TableHead><TableHead>Cliente</TableHead>
                <TableHead>Polizza</TableHead><TableHead>Modalità</TableHead><TableHead>Sede</TableHead>
                <TableHead className="text-right">Importo</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(pdvDet.operazioni || []).length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Nessuna operazione da versare.</TableCell></TableRow>
                ) : pdvDet.operazioni.map((o) => (
                  <TableRow key={o.id} data-testid={`pnc-premi-op-${o.id}`}>
                    <TableCell><input type="checkbox" checked={pdvSel.has(o.id)} onChange={() => pdvToggle(o.id)} className="rounded border-slate-300" data-testid={`pnc-premi-chk-${o.id}`} /></TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{fmtIT(o.data)}</TableCell>
                    <TableCell className="text-xs">{o.cliente || "—"}</TableCell>
                    <TableCell className="text-xs">{o.numero_polizza || "—"}</TableCell>
                    <TableCell className="text-xs">{o.modalita_pagamento === "contante" ? "Contanti" : "Assegno"}</TableCell>
                    <TableCell className="text-xs">{o.sede || "—"}</TableCell>
                    <TableCell className="text-xs text-right font-medium">{euro(o.importo)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={pdvDistinta} data-testid="pnc-premi-distinta"><Receipt className="h-4 w-4 mr-1" /> Genera distinta</Button>
            {pdvDet.can_manage && (
              <>
                <Button onClick={() => pdvSegna("consegnato_agenzia")} disabled={pdvBusy || pdvSel.size === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="pnc-premi-segna-consegnato">
                  {pdvBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />} Segna consegnato in agenzia
                </Button>
                <Button onClick={() => pdvSegna("versato_banca")} disabled={pdvBusy || pdvSel.size === 0} variant="outline" className="border-emerald-300 text-emerald-700" data-testid="pnc-premi-segna-versato">
                  <Landmark className="h-4 w-4 mr-1" /> Segna versato in banca
                </Button>
              </>
            )}
            <Button variant="ghost" onClick={() => setPdvOpen(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
}
